import type { ViewSource } from "@e308/ux";
import type { AppIntent, AppState } from "./app-state-types.js";
import {
  appendEvents,
  availableMaps,
  initialAppState,
  initialMilitaryDraft,
  settledCommandFeedback,
} from "./app-state-utils.js";
import type { WorkerPort } from "./app-types.js";
import { ArmySharePreview } from "./army-share-preview.js";
import { PracticeAdvisor } from "./guidance.js";
import { HOST_PROTOCOL_VERSION, type HostMessage } from "./host-protocol.js";
import { practiceConfig } from "./practice-config.js";

export type * from "./app-state-types.js";
export type * from "./app-types.js";

export class BrowserAppSource implements ViewSource<AppState, AppIntent, void> {
  #state: AppState = initialAppState;
  readonly #listeners = new Set<(state: AppState) => void>();
  readonly #worker: WorkerPort;
  #feedbackSequence = 0;
  readonly #advisor = new PracticeAdvisor();
  readonly #armyPreview = new ArmySharePreview();
  readonly #onMessage = (event: MessageEvent<HostMessage>) => this.receive(event.data);

  constructor(worker: WorkerPort) {
    this.#worker = worker;
    worker.addEventListener("message", this.#onMessage);
  }

  getSnapshot(): AppState {
    return this.#state;
  }

  subscribe(listener: (state: AppState) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  dispatch(intent: AppIntent): void {
    if (intent.kind === "preview-army-share" || intent.kind === "cancel-army-preview") {
      if (intent.kind === "cancel-army-preview") this.#armyPreview.cancel();
      else if (this.#state.observation)
        this.#armyPreview.preview(this.#state.observation, intent.borderId, intent.value);
      this.update({});
      return;
    }
    if (
      intent.kind === "restart" ||
      intent.kind === "start-practice" ||
      intent.kind === "import-artifact" ||
      intent.kind === "exit-replay"
    )
      this.#armyPreview.reset();
    if (this.dispatchGuidance(intent)) return;
    this.dispatchOrders(intent);
  }

  private dispatchGuidance(intent: AppIntent): boolean {
    if (intent.kind === "set-advisor") {
      this.#advisor.reset();
      this.update({ advisorEnabled: intent.enabled });
      return true;
    }
    if (intent.kind === "dismiss-advice") {
      this.#advisor.dismiss(intent.id);
      this.update({});
      return true;
    }
    if (intent.kind === "open-guidance") {
      this.update({
        navigation: {
          sequence: (this.#state.navigation?.sequence ?? 0) + 1,
          guidance: intent.guidance,
        },
      });
      return true;
    }
    return false;
  }

  private dispatchOrders(intent: AppIntent): void {
    if (this.dispatchK17(intent) || this.dispatchDraft(intent)) return;
    if (intent.kind === "start-practice") {
      this.start();
      return;
    }
    if (intent.kind === "game-order") {
      if (this.#state.phase === "playing") {
        const targetId = intent.sourceId ?? intent.intent.kind;
        const requestId = `practice-feedback-${++this.#feedbackSequence}`;
        if (
          intent.intent.kind === "adjust-army" &&
          intent.intent.field === "share" &&
          this.#state.observation
        )
          this.#armyPreview.commit(
            this.#state.observation,
            intent.intent.borderId,
            intent.intent.value,
            requestId,
          );
        this.update({
          error: undefined,
          combatForecast: undefined,
          commandFeedback: { requestId, targetId, state: "pending", message: "Submitting action…" },
        });
        this.#worker.postMessage({ kind: "submit", feedbackId: requestId, intent: intent.intent });
      }
      return;
    }
    if (intent.kind === "request-combat-forecast") {
      if (this.#state.phase === "playing")
        this.#worker.postMessage({ kind: "forecast-combat", horizonMs: 60_000 });
      return;
    }
    if (intent.kind === "set-practice-paused") {
      if (this.#state.phase === "playing")
        this.#worker.postMessage({ kind: "set-paused", paused: intent.paused });
      return;
    }
    if (intent.kind === "restart") {
      this.#worker.postMessage({ kind: "dispose" });
      this.update({
        phase: "lobby",
        militaryDraft: {},
        observation: undefined,
        error: undefined,
        workerOwned: false,
        combatForecast: undefined,
        events: [],
        chat: [],
        artifact: undefined,
        replayActive: false,
        paused: false,
        commandFeedback: undefined,
      });
      return;
    }
    const lobby = { ...this.#state.lobby };
    if (intent.kind === "set-town-name") lobby.townName = intent.value;
    else if (intent.kind === "set-ai-count") {
      lobby.aiCount = intent.value;
      if (!availableMaps(intent.value).includes(lobby.mapId))
        lobby.mapId = intent.value === 1 ? "duel" : "ring";
    } else if (intent.kind === "set-map") lobby.mapId = intent.value;
    else if (intent.kind === "set-duration") lobby.durationPreset = intent.value;
    else if (intent.kind === "set-clock-speed") lobby.clockSpeed = intent.value;
    else if (intent.kind === "set-bot-personality") lobby.botPersonality = intent.value;
    else if (intent.kind === "set-bot-difficulty") lobby.botDifficulty = intent.value;
    else return;
    this.update({ lobby });
  }

  private dispatchDraft(intent: AppIntent): boolean {
    if (intent.kind === "set-queue-draft") {
      this.update({
        drafts: {
          ...this.#state.drafts,
          [intent.queue]: { ...this.#state.drafts[intent.queue], ...intent.change },
        },
      });
      return true;
    }
    if (intent.kind === "set-military-draft") {
      if (!this.#state.observation?.self.logistics.borders[intent.borderId]) return true;
      this.update({
        militaryDraft: {
          ...this.#state.militaryDraft,
          [intent.borderId]: {
            ...(this.#state.militaryDraft[intent.borderId] ?? initialMilitaryDraft),
            ...intent.change,
          },
        },
      });
      return true;
    }
    return false;
  }

  dispose(): void {
    this.#worker.postMessage({ kind: "dispose" });
    this.#worker.removeEventListener("message", this.#onMessage);
    this.#worker.terminate();
    this.#listeners.clear();
  }

  private dispatchK17(intent: AppIntent): boolean {
    if (intent.kind === "set-valley-draft")
      this.update({ valleyDraft: { ...this.#state.valleyDraft, ...intent.change } });
    else if (intent.kind === "set-log-filter") this.update({ logFilter: intent.value });
    else if (intent.kind === "send-local-chat") {
      const value = this.#state.valleyDraft.chatMessage.trim();
      if (value && this.#state.observation)
        this.update({
          chat: [
            ...this.#state.chat,
            {
              id: this.#state.chat.length + 1,
              atGameMs: this.#state.observation.gameTimeMs,
              sender: this.#state.observation.self.name,
              text: value.slice(0, 256),
            },
          ],
          valleyDraft: { ...this.#state.valleyDraft, chatMessage: "" },
        });
    } else if (intent.kind === "export-artifact") {
      if (this.#state.phase === "playing") this.#worker.postMessage(intent);
    } else if (intent.kind === "import-artifact") {
      this.#advisor.reset();
      this.update({ militaryDraft: {} });
      if (this.#state.phase === "playing")
        this.#worker.postMessage({ ...intent, value: this.#state.valleyDraft.importArtifact });
    } else if (intent.kind === "exit-replay") this.#worker.postMessage(intent);
    else return false;
    return true;
  }

  private start(): void {
    const name = this.#state.lobby.townName.trim();
    if (!name) {
      this.update({ phase: "error", error: "Town name is required." });
      return;
    }
    this.update({
      phase: "starting",
      militaryDraft: {},
      error: undefined,
      observation: undefined,
      workerOwned: false,
      combatForecast: undefined,
      events: [],
      chat: [],
      artifact: undefined,
      replayActive: false,
      paused: false,
      commandFeedback: undefined,
    });
    this.#worker.postMessage({
      kind: "start",
      protocolVersion: HOST_PROTOCOL_VERSION,
      seatId: "player",
      clockSpeed: this.#state.lobby.clockSpeed,
      config: practiceConfig(this.#state.lobby, name),
    });
  }

  private receive(message: HostMessage): void {
    this.#armyPreview.receive(message);
    if (message.kind === "ready") this.update({ phase: "lobby" });
    else if (message.kind === "observation")
      this.update({
        phase: "playing",
        observation: message.observation,
        workerOwned: message.workerOwned,
        events: appendEvents(this.#state.events, message.events),
      });
    else if (message.kind === "error") this.update({ phase: "error", error: message.message });
    else if (message.kind === "command-result") {
      const feedback = settledCommandFeedback(this.#state.commandFeedback, message);
      if (feedback) this.update({ error: undefined, commandFeedback: feedback });
    } else if (message.kind === "pause-state") this.update({ paused: message.paused });
    else if (message.kind === "combat-forecast") this.update({ combatForecast: message.forecast });
    else if (message.kind === "artifact")
      this.update({ artifact: { kind: message.artifactKind, value: message.value } });
    else if (message.kind === "replay-state") this.update({ replayActive: message.active });
    else if (message.kind === "command-error") {
      const feedback = settledCommandFeedback(this.#state.commandFeedback, message);
      if (feedback) this.update({ error: undefined, commandFeedback: feedback });
      else if (!message.feedbackId) this.update({ error: message.message });
    }
  }

  private update(change: Partial<AppState>): void {
    this.#state = { ...this.#state, ...change };
    if (this.#state.observation)
      this.#state = {
        ...this.#state,
        armyShares: this.#armyPreview.values(this.#state.observation),
        advice: this.#advisor.update(
          this.#state.observation,
          Boolean(
            this.#state.advisorEnabled &&
              !this.#state.replayActive &&
              this.#state.phase === "playing",
          ),
        ),
      };
    else {
      this.#advisor.reset();
      this.#state = { ...this.#state, advice: undefined };
    }
    for (const listener of this.#listeners) listener(this.#state);
  }
}
